/**
 * Backlog 200 — CLI version policy enforcement.
 *
 * Runs before every command. Three steps:
 *
 *   1. If CLOUDGRID_SKIP_VERSION_CHECK=1 → return immediately.
 *   2. Fetch /.well-known/cli-version-policy (2s timeout). Cache 5 min at
 *      ~/.cloudgrid/version-policy-cache.json. A fetch failure or stale
 *      cache miss collapses to "pass" so the platform being down never
 *      breaks the CLI.
 *   3. Compare the running CLI version against `min_version`. If below:
 *        - warn   → print one §23-voice line, continue.
 *        - prompt → ask y/N (default N). In non-TTY, downgrade to warn.
 *        - block  → print the block message + bypass hint, exit code 1.
 *
 * The legacy `update-check.ts` startup nudge (npm-registry poll) is
 * preserved alongside this — that one helps with ambient drift; this one
 * is the server-driven floor.
 */
import { readFileSync, writeFileSync, existsSync, mkdirSync } from 'node:fs';
import { join } from 'node:path';
import inquirer from 'inquirer';
import { getCloudGridHome } from './home.js';
import { loadConfig } from './config.js';
import { readCredentials } from './credentials.js';
import { getActiveOrgSlug } from './active-org.js';

const CACHE_FILE = 'version-policy-cache.json';
const CACHE_TTL_MS = 5 * 60 * 1000;
const FETCH_TIMEOUT_MS = 2000;
const INSTALL_COMMAND = 'npm install -g @cloudgrid-io/cli@latest';
const SKIP_ENV = 'CLOUDGRID_SKIP_VERSION_CHECK';

export interface PolicyWire {
  source: 'org' | 'global' | 'none';
  policy: {
    min_version: string;
    recommended_version: string;
    policy_level: 'warn' | 'prompt' | 'block';
    message_user?: string;
  } | null;
}

interface CacheShape {
  fetched_at: string;
  api_url: string;
  org_slug: string | null;
  payload: PolicyWire;
}

const SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)(?:-[A-Za-z0-9.-]+)?$/;

function parse(v: string): [number, number, number] | null {
  const m = SEMVER_RE.exec(v.trim());
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

export function isSemverLess(a: string, b: string): boolean {
  const pa = parse(a);
  const pb = parse(b);
  if (!pa || !pb) return false;
  for (let i = 0; i < 3; i++) {
    if (pa[i] < pb[i]) return true;
    if (pa[i] > pb[i]) return false;
  }
  return false;
}

export type Action =
  | { kind: 'pass' }
  | { kind: 'warn'; current: string; recommended: string; min: string; message?: string }
  | { kind: 'prompt'; current: string; recommended: string; min: string; message?: string }
  | { kind: 'block'; current: string; recommended: string; min: string; message?: string };

export function decidePolicyAction(currentVersion: string, wire: PolicyWire): Action {
  if (wire.source === 'none' || !wire.policy) return { kind: 'pass' };
  const { min_version, recommended_version, policy_level, message_user } = wire.policy;
  if (!isSemverLess(currentVersion, min_version)) return { kind: 'pass' };
  const base = {
    current: currentVersion,
    recommended: recommended_version,
    min: min_version,
    ...(message_user ? { message: message_user } : {}),
  };
  if (policy_level === 'warn') return { kind: 'warn', ...base };
  if (policy_level === 'prompt') return { kind: 'prompt', ...base };
  return { kind: 'block', ...base };
}

function cachePath(): string {
  return join(getCloudGridHome(), CACHE_FILE);
}

function readCache(apiUrl: string, orgSlug: string | null): PolicyWire | null {
  try {
    const p = cachePath();
    if (!existsSync(p)) return null;
    const cache = JSON.parse(readFileSync(p, 'utf-8')) as CacheShape;
    if (cache.api_url !== apiUrl) return null;
    if (cache.org_slug !== orgSlug) return null;
    const age = Date.now() - new Date(cache.fetched_at).getTime();
    if (age > CACHE_TTL_MS || age < 0) return null;
    return cache.payload;
  } catch {
    return null;
  }
}

function writeCache(apiUrl: string, orgSlug: string | null, payload: PolicyWire): void {
  try {
    mkdirSync(getCloudGridHome(), { recursive: true });
    writeFileSync(
      cachePath(),
      JSON.stringify(
        {
          fetched_at: new Date().toISOString(),
          api_url: apiUrl,
          org_slug: orgSlug,
          payload,
        } satisfies CacheShape,
        null,
        2,
      ),
    );
  } catch {
    /* best-effort */
  }
}

async function fetchPolicy(
  apiUrl: string,
  orgSlug: string | null,
  jwt: string | null,
): Promise<PolicyWire | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
    // BL588 Phase E PR-E3 — emit both the legacy `X-CloudGrid-Org` and the new
    // canonical `X-CloudGrid-Grid` header (same value); the well-known endpoint
    // accepts either. Phase F drops the org name.
    if (orgSlug) {
      headers['X-CloudGrid-Org'] = orgSlug;
      headers['X-CloudGrid-Grid'] = orgSlug;
    }
    const res = await fetch(`${apiUrl}/.well-known/cli-version-policy`, {
      headers,
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) return null;
    const body = (await res.json()) as PolicyWire;
    if (!body || typeof body.source !== 'string') return null;
    return body;
  } catch {
    return null;
  }
}

export interface EnforceOpts {
  /** Override the CLI version (test seam). */
  currentVersion?: string;
  /** Override the API base URL (test seam). */
  apiUrl?: string;
  /** Override the cached/fetched policy (test seam). */
  wire?: PolicyWire;
  /** Override the TTY detection (test seam). */
  isTty?: boolean;
  /** Override the inquirer prompt (test seam). */
  confirm?: (action: Extract<Action, { kind: 'prompt' }>) => Promise<boolean>;
  /** Override process.exit (test seam). */
  exit?: (code: number) => void;
  /** Test-only — skip the cache. */
  ignoreCache?: boolean;
}

/**
 * Main entry. Called once at CLI startup; never throws.
 */
export async function enforceVersionPolicy(
  currentVersion: string,
  opts: EnforceOpts = {},
): Promise<void> {
  if (process.env[SKIP_ENV] === '1') return;

  let apiUrl = opts.apiUrl;
  let orgSlug: string | null = null;
  let jwt: string | null = null;

  if (!apiUrl) {
    try {
      const cfg = loadConfig();
      apiUrl = cfg.api_url;
    } catch {
      return; // no config = pre-login; nothing to enforce against
    }
    try {
      orgSlug = getActiveOrgSlug();
    } catch {
      orgSlug = null;
    }
    try {
      jwt = readCredentials()?.jwt ?? null;
    } catch {
      jwt = null;
    }
  }

  if (!apiUrl) return;

  let wire: PolicyWire | null = opts.wire ?? null;
  if (!wire && !opts.ignoreCache) wire = readCache(apiUrl, orgSlug);
  if (!wire) {
    wire = await fetchPolicy(apiUrl, orgSlug, jwt);
    if (wire) writeCache(apiUrl, orgSlug, wire);
  }
  if (!wire) return; // network failure → no enforcement

  const action = decidePolicyAction(opts.currentVersion ?? currentVersion, wire);
  if (action.kind === 'pass') return;

  const isTty = opts.isTty ?? Boolean(process.stdin.isTTY && process.stdout.isTTY);
  const exit = opts.exit ?? ((code: number) => process.exit(code));
  const writeErr = (s: string): void => {
    process.stderr.write(s);
  };

  if (action.kind === 'warn') {
    if (action.message) writeErr(`${action.message}\n`);
    writeErr(
      `Notice: this CLI is ${action.current}. The recommended version is ${action.recommended}. Update with: ${INSTALL_COMMAND}\n`,
    );
    return;
  }

  if (action.kind === 'prompt') {
    if (!isTty) {
      // Non-interactive: downgrade to warn rather than blocking a script.
      if (action.message) writeErr(`${action.message}\n`);
      writeErr(
        `Notice: this CLI is ${action.current}. Version ${action.min} or newer is recommended. Update with: ${INSTALL_COMMAND}\n`,
      );
      return;
    }
    const confirm = opts.confirm ?? defaultConfirm;
    const ok = await confirm(action);
    if (!ok) {
      writeErr(`Update with: ${INSTALL_COMMAND}\n`);
      exit(1);
      return;
    }
    return;
  }

  // block
  if (action.message) writeErr(`${action.message}\n`);
  writeErr(
    `This CLI is ${action.current}. Version ${action.min} or newer is required to continue.\n\n` +
      `Update with: ${INSTALL_COMMAND}\n\n` +
      `To bypass this check, set ${SKIP_ENV}=1.\n`,
  );
  exit(1);
}

async function defaultConfirm(
  action: Extract<Action, { kind: 'prompt' }>,
): Promise<boolean> {
  const ans = await inquirer.prompt<{ ok: boolean }>([
    {
      type: 'confirm',
      name: 'ok',
      message: `This CLI is ${action.current}. Version ${action.min} or newer is recommended. Continue anyway?`,
      default: false,
    },
  ]);
  return ans.ok;
}
